/**
 * State codec — v3 only (Merkle directory tree state).
 *
 * State is a small JSON envelope with a rootDirHash pointing to the
 * content-addressed Merkle directory tree. Encrypted deterministically
 * so identical state → identical ciphertext → identical hash.
 */

import type { CryptoProvider } from '../types'

/** Persisted writer metadata (name, emoji) for multi-writer workspaces. */
export interface WriterStateEntry {
    kid: string
    pk: string
    name?: string
    emoji?: string
    addedAt: number
}

export interface WorkspaceState {
    type: 'secureblob-state-v3'
    /** SHA-256 hash of the encrypted root directory object. null = empty workspace. */
    rootDirHash: string | null
    /** When true, this commit is a tombstone — the workspace has been deleted. */
    deleted?: boolean
    /** Writer metadata — persisted so collaborator names/emojis survive syncs. */
    writers?: WriterStateEntry[]
}

// ── Canonical JSON ───────────────────────────────────────────────

export function canonicalize(value: unknown): string {
    if (value === null || typeof value !== 'object') return JSON.stringify(value)
    if (Array.isArray(value)) return `[${value.map(item => canonicalize(item)).join(',')}]`

    const entries = Object.entries(value as Record<string, unknown>)
        .filter(([, v]) => v !== undefined)
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))  // ordinal comparison — cross-platform deterministic
    return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${canonicalize(v)}`).join(',')}}`
}

// ── Codec deps ───────────────────────────────────────────────────

export interface StateCodecDeps {
    crypto: CryptoProvider
    stateKey: Uint8Array
    blobId: string
}

/** Build AAD bytes per Spec §4.3: UTF8("secureblob:v1|"+blobId+"|"+scope) */
export function buildAad(blobId: string, scope: 'state' | 'file' | 'dir'): Uint8Array {
    return new TextEncoder().encode(`secureblob:v1|${blobId}|${scope}`)
}

// ── Encrypt / Decrypt ────────────────────────────────────────────

/**
 * Encrypt state using content-addressed deterministic encryption.
 * Same state → same ciphertext → same hash (content-addressed dedup).
 */
export async function encryptState(
    state: WorkspaceState,
    deps: StateCodecDeps,
): Promise<{ ciphertext: Uint8Array; hash: string; canonical: Uint8Array }> {
    const canonical = new TextEncoder().encode(canonicalize(state))
    const aad = buildAad(deps.blobId, 'state')
    const ciphertext = await deps.crypto.encryptDeterministic(deps.stateKey, canonical, aad)
    const hash = await deps.crypto.sha256(ciphertext)
    return { ciphertext, hash, canonical }
}

/**
 * Decrypt state from encrypted bytes. Returns WorkspaceState (v3).
 */
export async function decryptState(ciphertext: Uint8Array, deps: StateCodecDeps): Promise<WorkspaceState> {
    const aad = buildAad(deps.blobId, 'state')
    const plaintext = await deps.crypto.decrypt(deps.stateKey, ciphertext, aad)
    return JSON.parse(new TextDecoder().decode(plaintext)) as WorkspaceState
}
